import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Button from "../components/reusableComponents/buttons/button11";

export const LoginScreen = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, error: authError, loading } = useAuth();

  // State for form data
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // State for form validation
  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });

  // State for login status
  const [loginStatus, setLoginStatus] = useState({
    isLoading: false,
    success: false,
    error: "",
  });

  // Password visibility toggle
  const [showPassword, setShowPassword] = useState(false);

  // Check if user is already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear errors when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  // Validate form
  const validateForm = () => {
    let isValid = true;
    const newErrors = { email: "", password: "" };

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
      isValid = false;
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoginStatus({ isLoading: true, success: false, error: "" });

    try {
      console.log("Attempting login with:", formData.email, formData.password);
      const result = await login(formData.email, formData.password);

      if (result.success) {
        setLoginStatus({
          isLoading: false,
          success: true,
          error: "",
        });

        // Redirect to dashboard
        navigate('/dashboard');
      } else {
        throw new Error(result.error || "Login failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      setLoginStatus({
        isLoading: false,
        success: false,
        error: error.message || authError || "Login failed. Please try again.",
      });
    }
  };

  // Handle forgot password
  const handleForgotPassword = () => {
    // Implement forgot password logic
    alert("Password reset link will be sent to your email if it exists in our system.");
  };

  // Handle signup redirect
  const handleSignUp = () => {
    // Implement signup redirect
    alert("Redirecting to signup page...");
    // navigate('/signup');
  };

  return (
    <div className="bg-[#f9f9f9] flex flex-row justify-center w-full min-h-screen">
      <div className="bg-[#f9f9f9] w-full max-w-[1440px] flex justify-center items-center py-8">
        <form
          onSubmit={handleSubmit}
          className="flex w-full max-w-[448px] items-center gap-2.5 p-8 bg-white rounded-2xl shadow-[0px_0px_20px_#0000001a]"
        >
          <div className="flex flex-col w-full items-center gap-4">
            <div className="relative self-stretch mt-[-1.00px] font-h5 font-[number:var(--h5-font-weight)] text-texttext-high text-[length:var(--h5-font-size)] text-center tracking-[var(--h5-letter-spacing)] leading-[var(--h5-line-height)] [font-style:var(--h5-font-style)]">
              Springlearns
            </div>

            <div className="flex flex-col items-center gap-8 relative self-stretch w-full">
              <p className="relative self-stretch mt-[-1.00px] font-h6 font-[number:var(--h6-font-weight)] text-texttext-medium text-[length:var(--h6-font-size)] text-center tracking-[var(--h6-letter-spacing)] leading-[var(--h6-line-height)] [font-style:var(--h6-font-style)]">
                Sign In To Your Account
              </p>

              {loginStatus.error && (
                <div className="w-full p-3 bg-red-100 text-red-700 rounded text-center">
                  {loginStatus.error}
                </div>
              )}

              {loginStatus.success && (
                <div className="w-full p-3 bg-green-100 text-green-700 rounded text-center">
                  Login successful!
                </div>
              )}

              <div className="flex flex-col items-start gap-6 relative self-stretch w-full">
                <div className="flex flex-col items-start gap-6 relative self-stretch w-full">
                  <div className="flex flex-col items-end gap-4 relative self-stretch w-full">
                    <div className="flex flex-col items-start gap-4 relative self-stretch w-full">
                      <div className="flex flex-col items-start gap-1 relative self-stretch w-full">
                        <div className="inline-flex items-start relative">
                          <div className="inline-flex gap-1 items-center relative">
                            <div className="relative w-fit mt-[-1.00px] font-body-1 font-[number:var(--body-1-font-weight)] text-texttext-high text-[length:var(--body-1-font-size)] text-center tracking-[var(--body-1-letter-spacing)] leading-[var(--body-1-line-height)] whitespace-nowrap [font-style:var(--body-1-font-style)]">
                              Email
                            </div>
                          </div>
                        </div>

                        <div className={`flex items-center gap-1 px-2 py-2.5 relative self-stretch w-full bg-white rounded border border-solid ${errors.email ? 'border-red-500' : 'border-[#d6d6d6]'}`}>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter your email"
                            className="relative flex-1 mt-[-1.00px] font-body-2 font-[number:var(--body-2-font-weight)] text-texttext-high text-[length:var(--body-2-font-size)] tracking-[var(--body-2-letter-spacing)] leading-[var(--body-2-line-height)] [font-style:var(--body-2-font-style)] outline-none w-full"
                          />
                        </div>
                        {errors.email && (
                          <div className="text-red-500 text-sm">{errors.email}</div>
                        )}
                      </div>

                      <div className="flex flex-col items-start gap-1 relative self-stretch w-full">
                        <div className="inline-flex items-start relative">
                          <div className="inline-flex items-center gap-1 relative">
                            <div className="relative w-fit mt-[-1.00px] font-body-1 font-[number:var(--body-1-font-weight)] text-texttext-high text-[length:var(--body-1-font-size)] text-center tracking-[var(--body-1-letter-spacing)] leading-[var(--body-1-line-height)] whitespace-nowrap [font-style:var(--body-1-font-style)]">
                              Password
                            </div>
                          </div>
                        </div>

                        <div className={`flex items-center gap-1 px-2 py-2.5 relative self-stretch w-full bg-white rounded border border-solid ${errors.password ? 'border-red-500' : 'border-[#d6d6d6]'}`}>
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Enter your password"
                            className="relative flex-1 mt-[-1.00px] font-body-2 font-[number:var(--body-2-font-weight)] text-texttext-high text-[length:var(--body-2-font-size)] tracking-[var(--body-2-letter-spacing)] leading-[var(--body-2-line-height)] [font-style:var(--body-2-font-style)] outline-none w-full"
                          />

                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="text-texttext-low hover:text-texttext-medium"
                          >
                            {showPassword ? (
                              <Eye className="w-4 h-4" />
                            ) : (
                              <EyeOff className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        {errors.password && (
                          <div className="text-red-500 text-sm">{errors.password}</div>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="relative self-stretch font-body-2 font-[number:var(--body-2-font-weight)] text-primaryprimary text-[length:var(--body-2-font-size)] text-right tracking-[var(--body-2-letter-spacing)] leading-[var(--body-2-line-height)] [font-style:var(--body-2-font-style)] hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>

                    <div className="w-full">
                      <button type="submit" style={{ display: 'none' }} />
                      <Button
                        text={loading || loginStatus.isLoading ? "Signing in..." : "Sign in"}
                        state="Hover"
                        type="Secondary"
                        variant="Default"
                        onClick={(e) => {
                          // Prevent default here to avoid double submission
                          e.preventDefault();
                          handleSubmit(e);
                        }}
                        fullWidth={true}
                        className="!p-0"
                        disabled={loading || loginStatus.isLoading}
                      />
                    </div>
                    

                  <div className="inline-flex items-center gap-1 relative">
                    <div className="relative w-fit mt-[-1.00px] font-body-2 font-[number:var(--body-2-font-weight)] text-texttext-medium text-[length:var(--body-2-font-size)] tracking-[var(--body-2-letter-spacing)] leading-[var(--body-2-line-height)] whitespace-nowrap [font-style:var(--body-2-font-style)]">
                      Don't have an account?
                    </div>

                    <button
                      type="button"
                      onClick={handleSignUp}
                      className="relative w-fit mt-[-1.00px] font-body-2 font-[number:var(--body-2-font-weight)] text-primaryprimary text-[length:var(--body-2-font-size)] tracking-[var(--body-2-letter-spacing)] leading-[var(--body-2-line-height)] whitespace-nowrap [font-style:var(--body-2-font-style)] hover:underline"
                    >
                      Sign up
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};